import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { Divider, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  DynamicInterviewMeetingHost,
  InterviewMeetingLocationModal_InterviewMeetingLocationSettingPrefFragment,
  InterviewMeetingLocationSettingPref,
  InterviewMeetingLocationSettingsInput,
  InterviewMeetingLocationType,
  SettingLevel,
  VideoMeetingLinkScope,
} from 'src/generated/mloop-graphql';

import InterviewMeetingLocationHost from './InterviewMeetingLocationHost';
import InterviewMeetingLocationMenu from './InterviewMeetingLocationMenu';
import VideoMeetingLinkScopeRadioGroup from './VideoMeetingLinkScopeRadioGroup';
import { InterviewMeetingLocationVariant } from './types';
import { isLinkBearingMeetingLocationType } from './utils';

type Fragments = {
  interviewMeetingLocationSettingPref: InterviewMeetingLocationModal_InterviewMeetingLocationSettingPrefFragment;
};

type Props = {
  hideSyncWithOrgDefaultToggle?: boolean;
  settingLevel: SettingLevel;
  variant?: InterviewMeetingLocationVariant;
  orgInterviewMeetingLocationSetting: InterviewMeetingLocationSettingPref | null;
  onChange: (
    settingLevel: SettingLevel,
    interviewMeetingLocationSettingPref?: InterviewMeetingLocationSettingsInput
  ) => Promise<void>;
  onClose: () => void;
};

/**
 * Shared editor for an interview meeting-location setting, used by the org, job-stage, and task
 * surfaces. When the `org_video_meeting_link_scope` flag is on it also exposes a "Video meeting
 * links" scope radio group for link-bearing locations (forced to "unique" for interviewer-hosted
 * meetings).
 *
 * Pass `variant: DEBRIEF` to edit a debrief location setting instead: the copy refers to the debrief,
 * the link-scope control is never rendered, and video locations default to the scheduler as host.
 */
const InterviewMeetingLocationModal: FCWithFragments<Fragments, Props> = ({
  hideSyncWithOrgDefaultToggle = false,
  settingLevel,
  variant = InterviewMeetingLocationVariant.INTERVIEW,
  orgInterviewMeetingLocationSetting,
  interviewMeetingLocationSettingPref,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const orgVideoMeetingLinkScopeEnabled = useFlag('org_video_meeting_link_scope');
  const isDebrief = variant === InterviewMeetingLocationVariant.DEBRIEF;
  const [level, setLevel] = useState(settingLevel);
  const [saving, setSaving] = useState(false);
  const [meetingLocationSetting, setMeetingLocationSetting] = useState(interviewMeetingLocationSettingPref);

  const { enqueueSnackbar } = useSnackbar();

  const isSyncedFromOrg = level === SettingLevel.Org && !hideSyncWithOrgDefaultToggle;
  const disabled = isSyncedFromOrg;

  // When the meeting host is the interviewers, each interviewer hosts their own meeting so links are
  // structurally unique per interview — the scope is forced to UNIQUE (never ONE_LINK, which the
  // backend validator hard-rejects for interviewer-hosted prefs).
  const isInterviewersHost = meetingLocationSetting.dynamicHost === DynamicInterviewMeetingHost.Interviewers;

  // The scope only applies to link-bearing locations and only when the feature flag is on. Phone/text/
  // none locations (and, by extension, non-video prefs) never carry a scope. A debrief is a single
  // meeting, so the choice is meaningless there and the backend validator rejects any set scope.
  const showVideoMeetingLinkScope =
    !isDebrief &&
    orgVideoMeetingLinkScopeEnabled &&
    isLinkBearingMeetingLocationType(meetingLocationSetting.interviewMeetingLocationType);

  // Interviewer-hosted locations are forced to UNIQUE; otherwise reflect the stored scope, defaulting
  // an unset (NOT_SET) value to ONE_LINK so the radio group always shows an explicit choice.
  const resolvedScope = isInterviewersHost
    ? VideoMeetingLinkScope.UniqueLinkPerInterview
    : meetingLocationSetting.videoMeetingLinkScope;
  const videoMeetingLinkScopeValue =
    resolvedScope && resolvedScope !== VideoMeetingLinkScope.VideoMeetingLinkScopeNotSet
      ? resolvedScope
      : VideoMeetingLinkScope.OneLink;

  const handleVideoMeetingLinkScopeChange = (scope: VideoMeetingLinkScope) => {
    setMeetingLocationSetting((prev) => ({ ...prev, videoMeetingLinkScope: scope }));
  };

  const handleSyncedFromOrgChange = () => {
    if (level === SettingLevel.JobStage) {
      const orgSetting = orgInterviewMeetingLocationSetting || interviewMeetingLocationSettingPref;
      setLevel(SettingLevel.Org);
      setMeetingLocationSetting({
        ...orgSetting,
        videoMeetingLinkScope: orgSetting.videoMeetingLinkScope,
      });
    } else {
      setLevel(SettingLevel.JobStage);
    }
  };

  const handleMeetingLocationTypeChange = (type: InterviewMeetingLocationType) => {
    const isVideoLocationType = [
      InterviewMeetingLocationType.Zoom,
      InterviewMeetingLocationType.GoogleMeet,
      InterviewMeetingLocationType.MicrosoftTeams,
    ].includes(type);
    // Debriefs default to the scheduler as host: a debrief is one meeting, and the backend validator
    // rejects the INTERVIEWERS host for debrief prefs.
    let dynamicHost: DynamicInterviewMeetingHost | undefined;
    if (isVideoLocationType) {
      dynamicHost = isDebrief ? DynamicInterviewMeetingHost.Scheduler : DynamicInterviewMeetingHost.Interviewers;
    }

    setMeetingLocationSetting({
      interviewMeetingLocationType: type,
      dynamicHost,
      hostEmployeeId: undefined,
      customLocation: undefined,
      // Preserve the stored scope across a type change; never fabricate NOT_SET (that would clear it).
      videoMeetingLinkScope: meetingLocationSetting.videoMeetingLinkScope,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onChange(level, {
        interviewMeetingLocationType: meetingLocationSetting.interviewMeetingLocationType,
        dynamicHost: meetingLocationSetting.dynamicHost || undefined,
        hostEmployeeId: meetingLocationSetting.hostEmployeeId || undefined,
        remoteVideoMeetingHostUserId: meetingLocationSetting.remoteVideoMeetingHostUserId || undefined,
        customLocation: meetingLocationSetting.customLocation || undefined,
        // Only send a scope when the control is actually visible (flag on + link-bearing + not a
        // debrief) and this save writes a custom setting. Otherwise omit the field: GraphQL writes
        // preserve the stored value on an omitted/null scope, and synced job-stage saves inherit the
        // org value by writing no override. Interviewer-hosted prefs resolve to UNIQUE, never ONE_LINK
        // (which the backend validator rejects), and debrief prefs never carry a scope at all.
        videoMeetingLinkScope: showVideoMeetingLinkScope && !isSyncedFromOrg ? videoMeetingLinkScopeValue : undefined,
      });
      enqueueSnackbar(t('hiring-dashboard-common:common.location.saved'), { variant: 'success' });
      onClose();
    } catch {
      enqueueSnackbar(t('hiring-dashboard-common:common.location.failedToSave'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showMeetingHostSelect =
    meetingLocationSetting.interviewMeetingLocationType !== InterviewMeetingLocationType.None &&
    meetingLocationSetting.interviewMeetingLocationType !== InterviewMeetingLocationType.NotSpecified &&
    meetingLocationSetting.interviewMeetingLocationType !== InterviewMeetingLocationType.CandidatePhone;

  const isCustomLocationRequired =
    [
      InterviewMeetingLocationType.CustomPhone,
      InterviewMeetingLocationType.CustomText,
      InterviewMeetingLocationType.CustomLink,
    ].includes(meetingLocationSetting.interviewMeetingLocationType) && !meetingLocationSetting.customLocation;

  return (
    <Dialog
      open
      title={t('hiring-dashboard-common:common.location.title')}
      subTitle={
        isDebrief
          ? t('hiring-dashboard-common:common.location.subtitleDebrief')
          : t('hiring-dashboard-common:common.location.subtitle')
      }
      onClose={onClose}
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose, isDisabled: saving }}
      submitOptions={{
        label: t('hiring-dashboard-common:common.save'),
        onClick: handleSave,
        isDisabled: saving || isCustomLocationRequired,
        isLoading: saving,
        tooltip: isCustomLocationRequired ? t('hiring-dashboard-common:common.location.fillRequired') : undefined,
      }}
    >
      <Stack spacing={1.5}>
        {!hideSyncWithOrgDefaultToggle && (
          <FormControlLabel
            labelPlacement="start"
            sx={{ justifyContent: 'space-between', px: 0 }}
            label={<Typography>{t('hiring-dashboard-common:common.location.syncWithOrgSettings')}</Typography>}
            control={<Switch checked={level === SettingLevel.Org} onChange={handleSyncedFromOrgChange} />}
          />
        )}

        {!hideSyncWithOrgDefaultToggle && <Divider />}

        <Stack spacing={0.5}>
          <Typography variant="subtitle2">{t('hiring-dashboard-common:common.location.title')}</Typography>
          <InterviewMeetingLocationMenu
            disabled={disabled}
            variant={variant}
            interviewMeetingLocationType={meetingLocationSetting.interviewMeetingLocationType}
            onChange={handleMeetingLocationTypeChange}
          />
        </Stack>

        {showMeetingHostSelect && (
          <InterviewMeetingLocationHost
            disabled={disabled}
            variant={variant}
            interviewMeetingLocationSettingPref={meetingLocationSetting}
            onChange={(pref) => setMeetingLocationSetting(pref)}
          />
        )}

        {showVideoMeetingLinkScope && (
          <VideoMeetingLinkScopeRadioGroup
            value={videoMeetingLinkScopeValue}
            disabled={disabled}
            isInterviewersHost={isInterviewersHost}
            onChange={handleVideoMeetingLinkScopeChange}
          />
        )}
      </Stack>
    </Dialog>
  );
};

InterviewMeetingLocationModal.fragments = {
  interviewMeetingLocationSettingPref: gql`
    ${InterviewMeetingLocationHost.fragments.interviewMeetingLocationSettingPref}
    fragment InterviewMeetingLocationModal_interviewMeetingLocationSettingPref on InterviewMeetingLocationSettingPref {
      interviewMeetingLocationType
      videoMeetingLinkScope
      ...InterviewMeetingLocationHost_interviewMeetingLocationSettingPref
    }
  `,
};

export default InterviewMeetingLocationModal;
